import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    getIdToken
} from 'firebase/auth';
import { auth } from '../firebase';
import { isAdminBuild } from '../config/AppStatic';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Placeholder for fetching user role - future: call /api/me
const fetchUserRole = async (firebaseUid, token) => {
    if (!firebaseUid) return 'Student';
    // Temporary: treat authenticated users as Admin until /api/me is implemented using is_admin
    return 'Admin';
};

// Provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signupInProgress, setSignupInProgress] = useState(false);

    // Effect to handle auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            try {
                setError(null);

                // Skip auth state processing if signup is in progress
                if (signupInProgress) {
                    console.log("Auth state change ignored - signup in progress");
                    return;
                }

                // Handle anonymous access for /schedule in student/teacher builds only.
                if (!firebaseUser && window.location.pathname === '/schedule' && !isAdminBuild()) {
                    console.log("Anonymous access to schedule");
                    setUser({ uid: null, email: null, role: 'Student', token: null });
                    setToken(null);
                    setLoading(false);
                    return;
                }

                if (firebaseUser) {
                    console.log("Auth State Changed: User is logged in", firebaseUser.uid);
                    const token = await getIdToken(firebaseUser);
                    const role = await fetchUserRole(firebaseUser.uid, token);
                    const userData = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        role,
                        token
                    };
                    setUser(userData);
                    setToken(token);
                } else {
                    console.log("Auth State Changed: User is logged out");
                    setUser(null);
                    setToken(null);
                }
            } catch (error) {
                console.error("Error in auth state change:", error);
                setError(error.message || "Failed to initialize user session.");
                setUser(null);
                setToken(null);
                await firebaseSignOut(auth);
            } finally {
                setLoading(false);
            }
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, [signupInProgress]);

    // Effect to handle automatic token refresh
    useEffect(() => {
        let refreshInterval;
        let proactiveRefreshInterval;

        if (user && user.uid) {
            // Refresh token every 45 minutes (tokens expire after 1 hour, giving 15-minute buffer)
            refreshInterval = setInterval(async () => {
                try {
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        console.log("Refreshing Firebase ID token (scheduled)");
                        // Don't update any state - just refresh the token in Firebase
                        await getIdToken(currentUser, true); // Force refresh
                        console.log("Token refreshed successfully");
                    }
                } catch (error) {
                    console.error("Error refreshing token (scheduled):", error);
                    // Don't sign out immediately on refresh failure, let the API retry mechanism handle it
                    // Only sign out if it's a critical auth error
                    if (error.code === 'auth/user-token-expired' || error.code === 'auth/user-not-found') {
                        console.warn("Critical auth error, signing out user");
                        await signOut();
                    }
                }
            }, 45 * 60 * 1000); // 45 minutes

            // Proactive refresh every 30 minutes to ensure token is always fresh
            proactiveRefreshInterval = setInterval(async () => {
                try {
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        console.log("Proactive Firebase ID token refresh");
                        // Don't update any state - just refresh the token in Firebase
                        await getIdToken(currentUser, true); // Force refresh
                        console.log("Proactive token refresh completed");
                    }
                } catch (error) {
                    console.error("Error in proactive token refresh:", error);
                    // Don't sign out on proactive refresh failure
                }
            }, 30 * 60 * 1000); // 30 minutes
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
            if (proactiveRefreshInterval) {
                clearInterval(proactiveRefreshInterval);
            }
        };
    }, [user?.uid]);

    // Sign in function
    const signIn = async (email, password) => {
        try {
            setError(null);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const token = await getIdToken(userCredential.user);
            const role = await fetchUserRole(userCredential.user.uid, token);
            const userData = {
                uid: userCredential.user.uid,
                email: userCredential.user.email,
                role,
                token
            };
            setUser(userData);
            setToken(token);
            return userData;
        } catch (error) {
            setError(error);
            throw error;
        }
    };

    // Sign out function
    const signOut = async () => {
        try {
            await firebaseSignOut(auth);
            setUser(null);
            setToken(null);
        } catch (error) {
            setError(error);
            throw error;
        }
    };

    // Functions for managing signup state
    const startSignup = () => {
        console.log("Signup started - blocking auth state changes");
        setSignupInProgress(true);
    };

    const completeSignup = async (firebaseUser) => {
        console.log("Signup completed - processing auth state manually");
        try {
            if (firebaseUser) {
                const token = await getIdToken(firebaseUser);
                const role = await fetchUserRole(firebaseUser.uid, token);
                const userData = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    role,
                    token
                };
                setUser(userData);
                setToken(token);
            }
        } catch (error) {
            console.error("Error in completeSignup:", error);
            setError(error.message || "Failed to complete signup.");
            throw error;
        } finally {
            setSignupInProgress(false);
        }
    };

    const cancelSignup = () => {
        console.log("Signup cancelled - resuming normal auth state changes");
        setSignupInProgress(false);
    };

    // Function to get a fresh token on demand
    const refreshToken = async () => {
        try {
            const currentUser = auth.currentUser;
            if (currentUser) {
                console.log("Getting fresh Firebase ID token (on-demand)");
                const freshToken = await getIdToken(currentUser, true); // Force refresh
                // Don't update any state to avoid triggering effects
                // setToken(freshToken);
                // setUser(prev => ({ ...prev, token: freshToken }));
                console.log("On-demand token refresh completed");
                return freshToken;
            }
            throw new Error("No authenticated user");
        } catch (error) {
            console.error("Error getting fresh token (on-demand):", error);
            // If this is a critical auth error, sign out the user
            if (error.code === 'auth/user-token-expired' || error.code === 'auth/user-not-found') {
                console.warn("Critical auth error during on-demand refresh, signing out user");
                await signOut();
            }
            throw error;
        }
    };

    // Value object that will be provided to consumers
    const value = {
        user,
        token,
        loading,
        error,
        signIn,
        signOut,
        startSignup,
        completeSignup,
        cancelSignup,
        refreshToken
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext; 